import {
  POSTAddAdvancementCommentRequest,
  POSTAddFileAdvancementCommentReq,
  POSTAddFileAdvancementCommentRes,
  PostAddDenChiefRequest,
  PostAddDenChiefResponse,
  PostFetchAdvancementCommentsRequest,
  PostFetchAdvancementCommentsResponse,
  PostInviteDenChiefEmailRequest,
  PostRemoveDenChiefRequest,
  PostRemoveDenChiefResponse,
  PostSearchDenChiefRequest,
  PostSearchDenChiefResponse,
} from '@appTypes/esb';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';

const api = esbApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @esbEndpoint POST /advancements/v2/denChief/search
     */
    searchDenChief: builder.mutation<
      PostSearchDenChiefResponse,
      PostSearchDenChiefRequest
    >({
      query: body => ({
        url: '/advancements/v2/denChief/search',
        method: 'POST',
        body,
      }),
    }),
    /**
     * @esbEndpoint POST /advancements/v2/denChief/emailConfirmation
     */
    sendDenChiefEmail: builder.mutation<void, PostInviteDenChiefEmailRequest>({
      query: body => ({
        url: '/advancements/v2/denChief/emailConfirmation',
        method: 'POST',
        body,
      }),
    }),
    /**
     * @esbEndpoint POST /advancements/v2/users/:userId/comments
     */
    fetchAdvComments: builder.query<
      PostFetchAdvancementCommentsResponse,
      {
        youthUserId: number;
        payload: PostFetchAdvancementCommentsRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/users/${arg.youthUserId}/comments`,
        method: 'POST',
        body: arg.payload,
      }),
      providesTags: (result, _error, arg) =>
        result
          ? [
              // This is for the JS Edit & Delete
              tagFactory.AdvancementComment.forAdvancement(arg.youthUserId),
              tagFactory.AdvancementComment.forAdvancement(
                arg.youthUserId,
                arg.payload.advancementId,
              ),
              tagFactory.AdvancementComment.forAdvancementRequirement(
                arg.youthUserId,
                arg.payload.advancementId,
                arg.payload.requirementId,
              ),
            ]
          : [],
    }),

    /**
     * @esbEndpoint POST /advancements/v2/users/:userId/comments/add
     */
    addAdvComments: builder.mutation<
      void,
      {
        userId: number;
        payload: POSTAddAdvancementCommentRequest;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/users/${arg.userId}/comments/add`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              // The comments query is keyed per advancement (Sea Scout electives send ssElectiveId)
              tagFactory.AdvancementComment.forAdvancement(
                arg.payload.scoutUserId,
                arg.payload.advancementId ?? arg.payload.ssElectiveId,
              ),
            ],
    }),
    /**
     * @esbEndpoint POST /advancements/v2/users/:userId/comments/addFile
     */
    addFileAdvComments: builder.mutation<
      POSTAddFileAdvancementCommentRes,
      {
        userId: number;
        photo: File;
        payload: POSTAddFileAdvancementCommentReq;
      }
    >({
      queryFn: async (arg, _api, _opts, baseQuery) => {
        try {
          const addFile = await baseQuery({
            url: `/advancements/v2/users/${arg.userId}/comments/addFile`,
            method: 'POST',
            body: arg.payload,
          });

          const responseBody = addFile.data as POSTAddFileAdvancementCommentRes;

          await fetch(responseBody.preSignedUrl, {
            method: 'PUT',
            body: arg.photo,
            headers: { 'Content-Type': arg.photo.type },
          });

          return { data: responseBody };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              tagFactory.AdvancementComment.forAdvancementRequirement(
                arg.payload.scoutUserId,
                arg.payload.advancementId,
                arg.payload.requirementId,
              ),
              tagFactory.AdvancementComment.forAdvancement(
                arg.payload.scoutUserId,
                arg.payload.advancementId,
              ),
            ],
    }),

    /**
     * @esbEndpoint PUT /advancements/v2/users/:userId/comments/:commentId
     */
    editAdvComment: builder.mutation<
      void,
      {
        userId: number;
        commentId: number;
        advancementId: number;
        youthUserId: number;
        payload: {
          comments: string;
          subject: string;
          versionId?: number;
        };
      }
    >({
      query: arg => ({
        url: `/advancements/v2/users/${arg.userId}/comments/${arg.commentId}`,
        method: 'PUT',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              tagFactory.AdvancementComment.forAdvancement(
                arg.youthUserId,
                arg.advancementId,
              ),
            ],
    }),

    /**
     * @esbEndpoint DELETE /advancements/v2/users/:userId/comments/:commentId
     */
    deleteAdvComment: builder.mutation<
      void,
      {
        userId: number;
        commentId: number;
        youthUserId: number;
        advancementId: number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/users/${arg.userId}/comments/${arg.commentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              tagFactory.AdvancementComment.forAdvancement(
                arg.youthUserId,
                arg.advancementId,
              ),
            ],
    }),

    /**
     * @esbEndpoint DELETE /advancements/v2/users/:userId/comments/:commentId/photos/:photoId
     */
    deleteAdvCommentPhoto: builder.mutation<
      void,
      {
        userId: number;
        commentId: number;
        photoId: number;
        youthUserId: number;
        advancementId: number;
      }
    >({
      query: arg => ({
        url: `/advancements/v2/users/${arg.userId}/comments/${arg.commentId}/photos/${arg.photoId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error, arg) =>
        error
          ? []
          : [
              tagFactory.AdvancementComment.forAdvancement(
                arg.youthUserId,
                arg.advancementId,
              ),
            ],
    }),

    /**
     * @esbEndpoint POST /organizations/v2/:orgGuid/denChief/add
     */
    addDenChief: builder.mutation<
      PostAddDenChiefResponse,
      {
        orgGuid: string;
        payload: PostAddDenChiefRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.orgGuid}/denChief/add`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, { orgGuid }) =>
        error ? [] : [tagFactory.SubUnits.forOrg(orgGuid)],
    }),
    /**
     * @esbEndpoint POST /organizations/v2/:organizationGuid/denChief/remove
     */
    removeDenChief: builder.mutation<
      PostRemoveDenChiefResponse,
      {
        orgGuid: string;
        payload: PostRemoveDenChiefRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.orgGuid}/denChief/remove`,
        method: 'POST',
        body: arg.payload,
      }),
      invalidatesTags: (_result, error, { orgGuid, payload: { userId } }) =>
        error
          ? []
          : [
              tagFactory.YouthLeadershipPositions.forUser(userId),
              tagFactory.UnitPendingLeadershipPositions.forOrg(orgGuid),
              tagFactory.UnitRoster.organizationRosters(orgGuid),
              tagFactory.SubUnits.forOrg(orgGuid),
            ],
    }),
  }),
});

export const {
  useSendDenChiefEmailMutation,
  useSearchDenChiefMutation,
  useFetchAdvCommentsQuery,
  useLazyFetchAdvCommentsQuery,
  useAddAdvCommentsMutation,
  useAddFileAdvCommentsMutation,
  useEditAdvCommentMutation,
  useDeleteAdvCommentMutation,
  useDeleteAdvCommentPhotoMutation,
  useAddDenChiefMutation,
  useRemoveDenChiefMutation,
} = api;

export const denChiefApi = api;
